/**
 * Validation and migration of plan documents (TD §4 "Migration").
 * The server runs every saved plan through parsePlan, so nothing invalid is ever stored (R13).
 */
import { z } from 'zod';
import { PLAN_SCHEMA_VERSION, type PlanDoc } from './types';

/** Thrown when a plan document can't be read. `issues` lists what is wrong, in plain words. */
export class InvalidPlanError extends Error {
  constructor(public readonly issues: string[]) {
    super(`Invalid plan: ${issues.slice(0, 5).join('; ')}${issues.length > 5 ? ` (+${issues.length - 5} more)` : ''}`);
    this.name = 'InvalidPlanError';
  }
}

const num = z.number().finite();
const id = z.string().min(1).max(64);
const pt = z.tuple([num, num]);

const vert = z.object({ x: num, y: num, pinned: z.boolean().optional() }).strict();
const node = z.object({ x: num, y: num, w: num.min(0.5).max(100) }).strict();
const road = z.object({
  nodes: z.array(id).min(2),
  closed: z.boolean(),
  kind: z.enum(['main', 'cross', 'path', 'entrance']),
}).strict();
const river = z.object({ pts: z.array(pt).min(2), w: num.min(0.5).max(200) }).strict();
const animalArea = z.object({
  kind: z.enum(['habitat', 'aviary', 'aquarium']),
  verts: z.array(id).min(3),
  speciesId: id,
  count: z.number().int().min(1).max(500),
  locked: z.boolean(),
}).strict();
const otherArea = z.object({
  kind: z.enum(['lake', 'pool', 'plaza', 'backOfHouse']),
  verts: z.array(id).min(3),
  parent: id.optional(),
}).strict();
const icon = z.object({
  type: z.enum(['entrance', 'restaurant', 'gift', 'toilets', 'kiosk', 'info', 'staff', 'vet']),
  x: num, y: num,
  fixed: z.boolean().optional(),
}).strict();
const overlay = z.object({
  file: z.string().min(1).max(200),
  x: num, y: num,
  scale: num.positive(),
  rotation: num,
  opacity: num.min(0).max(1),
}).strict();
const terrain = z.object({ show: z.boolean(), hilliness: num.min(0).max(500), band: num.positive() }).strict();

const planShape = z.object({
  schemaVersion: z.literal(PLAN_SCHEMA_VERSION),
  name: z.string().max(200),
  size: num.min(50).max(5000),
  seed: z.number().int(),
  settings: z.object({ extraSpacePct: num.min(0).max(1000) }).strict(),
  plot: z.object({ verts: z.array(id).min(3), smooth: z.boolean() }).strict().nullable(),
  verts: z.record(id, vert),
  areas: z.record(id, z.union([animalArea, otherArea])),
  nodes: z.record(id, node),
  roads: z.record(id, road),
  rivers: z.record(id, river),
  icons: z.record(id, icon),
  unplaced: z.record(id, z.object({ speciesId: id, count: z.number().int().min(1).max(500) }).strict()),
  overlay: overlay.nullable(),
  terrain: terrain.nullable(),
}).strict();

/** Checks that every id reference points at something that exists. */
function referenceIssues(doc: PlanDoc): string[] {
  const out: string[] = [];
  const hasVert = (v: string) => Object.hasOwn(doc.verts, v);
  if (doc.plot) for (const v of doc.plot.verts) if (!hasVert(v)) out.push(`boundary uses missing corner ${v}`);
  for (const [aid, a] of Object.entries(doc.areas)) {
    for (const v of a.verts) if (!hasVert(v)) out.push(`area ${aid} uses missing corner ${v}`);
    if (new Set(a.verts).size < 3) out.push(`area ${aid} has fewer than 3 distinct corners`);
    if ('parent' in a && a.parent !== undefined) {
      const p = doc.areas[a.parent];
      if (!p) out.push(`pool ${aid} belongs to missing habitat ${a.parent}`);
      else if (p.kind !== 'habitat' && p.kind !== 'aviary' && p.kind !== 'aquarium') out.push(`pool ${aid} belongs to ${a.parent}, which is not an animal area`);
    }
  }
  for (const [rid, r] of Object.entries(doc.roads)) {
    for (const n of r.nodes) if (!Object.hasOwn(doc.nodes, n)) out.push(`road ${rid} uses missing dot ${n}`);
    if (new Set(r.nodes).size < 2) out.push(`road ${rid} has fewer than 2 distinct dots`);
  }
  return out;
}

/**
 * Upgrades an older document to the current schemaVersion, one step at a time.
 * 1 → 2: the Unplaced tray moved into the plan (it used to live in one browser); old plans start with an empty tray.
 */
function migrate(raw: Record<string, unknown>): Record<string, unknown> {
  let doc = raw;
  if (doc.schemaVersion === 1) doc = { ...doc, schemaVersion: 2, unplaced: {} };
  if (doc.schemaVersion === PLAN_SCHEMA_VERSION) return doc;
  throw new InvalidPlanError([`unknown schemaVersion ${JSON.stringify(raw.schemaVersion)} (this app reads version ${PLAN_SCHEMA_VERSION})`]);
}

/**
 * Validates (and if needed migrates) a plan document. Accepts parsed JSON or a JSON string.
 * Throws InvalidPlanError listing every problem found.
 */
export function parsePlan(input: unknown): PlanDoc {
  let raw: unknown = input;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { throw new InvalidPlanError(['not valid JSON']); }
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) throw new InvalidPlanError(['not an object']);
  const migrated = migrate(raw as Record<string, unknown>);
  const result = planShape.safeParse(migrated);
  if (!result.success) {
    throw new InvalidPlanError(result.error.issues.map(i => `${i.path.join('.') || '(root)'}: ${i.message}`));
  }
  const doc: PlanDoc = result.data;
  const refs = referenceIssues(doc);
  if (refs.length) throw new InvalidPlanError(refs);
  return doc;
}

/** A new, empty plan: no boundary, no roads, nothing placed. */
export function emptyPlan(size: number, name = 'My zoo'): PlanDoc {
  return {
    schemaVersion: PLAN_SCHEMA_VERSION,
    name,
    size,
    seed: 1,
    settings: { extraSpacePct: 25 },
    plot: null,
    verts: {},
    areas: {},
    nodes: {},
    roads: {},
    rivers: {},
    icons: {},
    unplaced: {},
    overlay: null,
    terrain: null,
  };
}

/** Short random id for plan objects (verts, areas, nodes, …): 10 base-36 characters. */
export function newId(): string {
  const bytes = new Uint8Array(8);
  globalThis.crypto.getRandomValues(bytes);
  let s = '';
  for (const b of bytes) s += (b % 36).toString(36);
  return s + Date.now().toString(36).slice(-2);
}
